import React, { useState } from "react"

const pendingBadge =
  "ml-2 inline-flex items-center rounded-full bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const selectCls =
  "rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm text-gray-700 dark:text-gray-300 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-50"
const dangerBtn =
  "text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
const linkBtn =
  "text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-800 disabled:opacity-40 disabled:cursor-not-allowed"

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })

export function TeamTable({ team, setTeam, membershipsPath, currentPartnerId, canManageOwner, currentUserId, copy }) {
  const [busyId, setBusyId] = useState(null)
  const [errorId, setErrorId] = useState(null)
  const [resentId, setResentId] = useState(null)

  const membershipUrl = (id) => `${membershipsPath}/${id}`

  const changeRole = async (member, newRole) => {
    if (busyId) return
    setBusyId(member.id)
    setErrorId(null)
    try {
      const res = await fetch(membershipUrl(member.id), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify({ membership: { role: newRole } }),
      })
      if (res.ok) {
        const updated = await res.json()
        setTeam((prev) => prev.map((m) => (m.id === member.id ? { ...m, role: updated.role } : m)))
      } else {
        setErrorId(member.id)
      }
    } catch {
      setErrorId(member.id)
    } finally {
      setBusyId(null)
    }
  }

  const resendInvite = async (member) => {
    if (busyId) return
    setBusyId(member.id)
    setErrorId(null)
    setResentId(null)
    try {
      const res = await fetch(`${membershipUrl(member.id)}/resend`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (res.ok) {
        setResentId(member.id)
      } else {
        setErrorId(member.id)
      }
    } catch {
      setErrorId(member.id)
    } finally {
      setBusyId(null)
    }
  }

  const transferOwnership = async (member) => {
    if (!window.confirm(copy.transfer.confirm)) return
    if (busyId) return
    setBusyId(member.id)
    setErrorId(null)
    try {
      const res = await fetch(`${membershipUrl(member.id)}/transfer_ownership`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (res.ok) {
        // Target becomes owner; the previous owner drops to admin.
        setTeam((prev) =>
          prev.map((m) => {
            if (m.id === member.id) return { ...m, role: "owner" }
            if (m.role === "owner") return { ...m, role: "admin" }
            return m
          })
        )
      } else {
        setErrorId(member.id)
      }
    } catch {
      setErrorId(member.id)
    } finally {
      setBusyId(null)
    }
  }

  const removeMember = async (member) => {
    if (!window.confirm(member.invitationPending ? copy.revokeConfirm : copy.removeConfirm)) return
    if (busyId) return
    setBusyId(member.id)
    setErrorId(null)
    try {
      const res = await fetch(membershipUrl(member.id), {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      if (res.ok) {
        setTeam((prev) => prev.filter((m) => m.id !== member.id))
      } else {
        setErrorId(member.id)
      }
    } catch {
      setErrorId(member.id)
    } finally {
      setBusyId(null)
    }
  }

  const isOwner = (m) => m.role === "owner"
  const isSelf = (m) => String(m.userId) === String(currentPartnerId)

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">{copy.columns.member}</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">{copy.columns.role}</th>
            <th className="hidden px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400 sm:table-cell">{copy.columns.joined}</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">{copy.columns.actions}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
          {team.map((member) => (
            <tr key={member.id}>
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {member.name}
                  {member.invitationPending && <span className={pendingBadge}>{copy.pending}</span>}
                </div>
                <div className="text-gray-500 dark:text-gray-400">{member.email}</div>
              </td>
              <td className="px-4 py-3">
                {isOwner(member) ? (
                  <span className="inline-flex items-center rounded-full bg-brand-50 dark:bg-brand-950 px-2.5 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-300">
                    {copy.roles.owner}
                  </span>
                ) : (
                  <select
                    value={member.role}
                    disabled={busyId === member.id}
                    onChange={(e) => changeRole(member, e.target.value)}
                    className={selectCls}
                  >
                    <option value="member">{copy.roles.member}</option>
                    <option value="admin">{copy.roles.admin}</option>
                  </select>
                )}
                {errorId === member.id && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{copy.errorGeneric}</p>
                )}
              </td>
              <td className="hidden px-4 py-3 text-gray-500 dark:text-gray-400 sm:table-cell">
                {fmtDate(member.joinedAt)}
              </td>
              <td className="px-4 py-3">
                {!isOwner(member) && (
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-3">
                      {canManageOwner && !member.invitationPending && (
                        <button
                          onClick={() => transferOwnership(member)}
                          disabled={busyId === member.id}
                          className={linkBtn}
                        >
                          {copy.transfer.action}
                        </button>
                      )}
                      {member.invitationPending && (
                        <button
                          onClick={() => resendInvite(member)}
                          disabled={busyId === member.id}
                          className={linkBtn}
                        >
                          {copy.resend}
                        </button>
                      )}
                      <button
                        onClick={() => removeMember(member)}
                        disabled={busyId === member.id}
                        className={dangerBtn}
                      >
                        {member.invitationPending ? copy.revoke : copy.remove}
                      </button>
                    </div>
                    {resentId === member.id && (
                      <span className="text-xs text-green-600 dark:text-green-400">{copy.resendSuccess}</span>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
